import {Link, useLocation} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {motion, AnimatePresence} from 'framer-motion';
import {authSelector} from "../../../redux/slices/authSlice";
import {
    financeSelector,
    setBalance,
    setTopUpBalanceActive,
} from "../../../redux/slices/financeSlice";
import {
    fetchNotificationsList,
    globalDataSelector,
    setAdaptiveMenu,
    setExitPopup,
    setIsCollapsedSidebar,
} from "../../../redux/slices/globalDataSlice";
import React, { useEffect, useRef, useState} from "react";
import {useWindowWidth} from "../../../hooks/useWindowWidth";
import {
    checkAllTokens,
    pixelApiSelector,
} from "../../../redux/slices/pixelApiSlice";
import {
    decodeUserJwt,
    getImpersonateUserToken,
    getUser, is_allowed_statistics_access, is_superuser,
    removeItem, user_role,
} from "../../../storage/user";
import s from "./newSidebar.module.scss";
import SvgSelector from "../../SvgSelector/SvgSelector";
import AnimatedBalance from "../../UiKit/AnimatedBalance/AnimatedBalance";
import SidebarMuiTooltip from "../../UiKit/MuiTooltip/SidebarToolTip";
import {
    OWNER,
    sidebarOptionsList,
} from "../../../utils/vars/staticVariables";
import MuiTooltip from "../../UiKit/MuiTooltip/MuiTooltip";
import GradientBorder from "../../GradientBorder/GradientBorder";
import useWebSocket from "react-use-websocket";
import {
    getFinanceWebSocket,
    wsOptions,
    wsSource,
} from "../../../api/WebSocket";
import {useTranslation} from "react-i18next";
import {setSidebarWidth} from "../../../storage/sidebarWidth";
import {
    fetchSubscriptions,
    setSubscriptionEndingPopupActive,
    subscriptionSelector
} from "../../../redux/slices/subscriptionSlice";
import SubscriptionEndingInfoPopup
    from "../../../pages/Authorized/SpecialAccess/Subscriptions/Popups/SubscriptionEndingInfoPopup/SubscriptionEndingInfoPopup";
import {isSubscriptionEndingNotifiedLimit1, isSubscriptionEndingNotifiedLimit3} from "../../../storage/subscription";
import {fetchAllUsers} from "../../../redux/slices/teamsSlice";
import {getTableDefaultParams} from "../../../api/helpers/scripts";
import InstallsEndingInfoPopup
    from "../../../pages/Authorized/SpecialAccess/Subscriptions/Popups/InstallsEndingInfoPopup/InstallsEndingInfoPopup";

const STORAGE_KEY = 'sidebar-open-sections';

const containerVariants = {
    collapsed: {
        height: 0,
        opacity: 0,
        transition: {
            when: 'afterChildren',
            staggerChildren: 0.05,
            staggerDirection: -1
        }
    },
    open: {
        height: 'auto',
        opacity: 1,
        transition: {
            when: 'beforeChildren',
            staggerChildren: 0.1
        }
    }
};

const NewSidebar = () => {
    const sidebarRef = useRef(null);
    const sidebarContainerRef = useRef(null);
    const sidebarWrapperRef = useRef(null);
    const lastLinkRefs = useRef({});

    const [userIconType, setUserIconType] = useState(null);
    const [openSections, setOpenSections] = useState({});
    const isInitialMount = useRef(true);

    const location = useLocation();
    const dispatch = useDispatch();
    const {isPartner, areHiddenFinancialOperations} = useSelector(authSelector);
    const {balance} = useSelector(financeSelector);
    const {isCollapsedSidebar, exitPopup, isAdaptiveMenuActive} =
        useSelector(globalDataSelector);
    const {isValidPixelApiTokens} = useSelector(pixelApiSelector);

    const {lastMessage} = useWebSocket(getFinanceWebSocket, wsOptions);

    const width = useWindowWidth();

    const {t} = useTranslation();

    useEffect(() => {
        setTimeout(() => {
            isInitialMount.current = false;
        }, 700)
    }, []);

    useEffect(() => {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) setOpenSections(JSON.parse(stored));
    }, []);

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(openSections));
    }, [openSections]);

    const toggleSection = (title) =>
        setOpenSections(prev => ({...prev, [title]: !prev[title]}));

    useEffect(() => {
        if (isPartner !== null) {
            if (isPartner === true) {
                setUserIconType("partner-user-icon");
            } else if (is_allowed_statistics_access && !is_superuser) {
                setUserIconType("support-icon");
            } else if (is_superuser) {
                setUserIconType("superuser-icon");
            } else {
                setUserIconType("default-user-icon");
            }
        }
    }, [isPartner]);

    useEffect(() => {
        if (!lastMessage) return;

        const parsedMessage = JSON.parse(lastMessage.data);

        if (parsedMessage?.source === wsSource.balance) {
            dispatch(setBalance(parsedMessage?.data?.new_balance));
        }
    }, [dispatch, lastMessage]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                sidebarWrapperRef.current &&
                !sidebarWrapperRef.current.contains(event.target) &&
                sidebarContainerRef.current &&
                sidebarContainerRef.current.contains(event.target)
            ) {
                dispatch(setAdaptiveMenu(false));
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [dispatch]);

    useEffect(() => {
        if (isAdaptiveMenuActive && width <= 1090) {
            document.body.style.overflow = "hidden";
            if (sidebarRef.current) {
                sidebarRef.current.focus();
            }
        } else {
            document.body.style.overflow = "";
        }
    }, [isAdaptiveMenuActive, width]);

    useEffect(() => {
        dispatch(checkAllTokens());
    }, [dispatch]);

    useEffect(() => {
        dispatch(fetchSubscriptions())
    }, [dispatch])


    useEffect(() => {
        const fetchTeamUsers = () => {
            return dispatch(
                fetchAllUsers(
                    getTableDefaultParams({}),
                ),
            );
        };

        fetchTeamUsers()

    }, [dispatch]);

    useEffect(() => {
        dispatch(fetchNotificationsList({page: 1}));
    }, [dispatch]);

    const onSidebarWidthHandler = () => {
        const booleanValue = isCollapsedSidebar === "true";
        if (booleanValue) {
            dispatch(setIsCollapsedSidebar("false"));
            setSidebarWidth("false");
        } else {
            dispatch(setIsCollapsedSidebar("true"));
            setSidebarWidth("true");
        }
    };

    useEffect(() => {
        dispatch(setIsCollapsedSidebar(isCollapsedSidebar));
        setSidebarWidth(isCollapsedSidebar);
    }, [dispatch, isCollapsedSidebar]);

    const userData = getUser();
    const user = userData ? userData?.user : null;

    const fullScreen = `${s.sidebarWrapper} ${isCollapsedSidebar === "true" ? s.collapsed : ""}`;
    const adaptiveScreen = `${s.sidebarWrapper} ${isAdaptiveMenuActive ? s.activeMenu : s.closedMenu}`;

    const adminEmail = decodeUserJwt(localStorage.getItem("accessToken"))?.user?.email

    const {subscriptionsList} = useSelector(subscriptionSelector);

    const activeSubscription = subscriptionsList.find(item => item?.is_active === true)

    useEffect(() => {
        if (user_role === OWNER) {
            const {end_date_count} = activeSubscription || {};
            const isNotificationLimit3Reached = isSubscriptionEndingNotifiedLimit3 === 'true';
            const isNotificationLimit1Reached = isSubscriptionEndingNotifiedLimit1 === 'true';

            if (
                (end_date_count < 4 && end_date_count > 1 && !isNotificationLimit3Reached) ||
                (end_date_count === 1 && !isNotificationLimit1Reached)
            ) {
                dispatch(setSubscriptionEndingPopupActive(true));
            }
        }
    }, [activeSubscription?.end_date_count, dispatch]);


    return (
        <div
            className={`${s.sidebarContainer} ${isAdaptiveMenuActive ? s.activeMenu : s.closedMenu}`}
            ref={sidebarContainerRef}
        >
            <div
                className={width > 1090 ? fullScreen : adaptiveScreen}
                ref={sidebarWrapperRef}
            >
                <InstallsEndingInfoPopup/>
                <SubscriptionEndingInfoPopup/>
                <div className={s.sidebarHeader}>
                    <div className={s.info}>
                        <div className={s.balanceInfoContainer}>
                            <Link to="/account" className={s.userIconContainer}>
                                <SvgSelector id={userIconType}/>
                            </Link>
                            {!areHiddenFinancialOperations && typeof areHiddenFinancialOperations === "boolean" && (
                                <button
                                    className={s.balanceBtn}
                                    onClick={() => {
                                        dispatch(setAdaptiveMenu(false));
                                        dispatch(setTopUpBalanceActive(true));
                                    }}
                                >
                                    <AnimatedBalance end={balance}/>
                                    <span className={s.plus}>+</span>
                                </button>
                            )}
                        </div>
                        <div className={s.userInfoContainer}>
                            <p className={s.email}>
                                {user?.email}
                                <MuiTooltip title={user?.email}></MuiTooltip>
                            </p>
                        </div>
                    </div>
                    <button
                        className={`${s.changeWidthBtn} ${isCollapsedSidebar === "true" ? s.rotated : ""}`}
                        onClick={onSidebarWidthHandler}
                    >
                        <SvgSelector id="openSidebarArrow"/>
                    </button>
                </div>
                <div className={s.gradientContainer}>
                    <GradientBorder className={s.gradientBorderCustom}/>
                </div>

                {getImpersonateUserToken() && (
                    <div className={s.returnToAdminContainer}>
                        <button
                            className={s.returnToAdminButton}
                            onClick={() => {
                                removeItem("impersonateUserToken");
                                window.location.href = "/management";
                            }}
                        >
                            <div className={s.returnIconContainer}>
                                <SvgSelector id="return-to-adimn-acc-icon"/>
                            </div>
                            <div className={s.btnDesc}>
                                <span className={s.desc}>{t('back_to_account')}</span>
                                <span className={s.email}>
                                  {adminEmail}
                                </span>
                            </div>
                        </button>
                    </div>
                )}


                <div className={s.sidebarContentContainer}>
                    <div className={s.sidebar} ref={sidebarRef} tabIndex="-1">
                        {sidebarOptionsList.slice(0, -1).map(({title, pages, icon}) => {
                                const isSectionActive = pages.some(page => location.pathname.startsWith(page.path));

                                return (
                                    <div
                                        className={`${s.chapterWrapper} ${isSectionActive ? s.active : ''}`}
                                        key={title}
                                    >
                                        <button
                                            className={s.chapterHeader}
                                            onClick={() => toggleSection(title)}
                                            type="button"
                                        >
                                            <div className={s.iconWrapper}>
                                                {isCollapsedSidebar === "true" ? (
                                                    <SidebarMuiTooltip title={t(title)}>
                                                        <SvgSelector id={icon} />
                                                    </SidebarMuiTooltip>
                                                ) : (
                                                    <SvgSelector id={icon} />
                                                )}
                                            </div>
                                            <span className={s.title}>{t(title)}</span>
                                            <span
                                                className={`${s.arrowIcon} ${openSections[title] ? s.open : ''}`}
                                            >
                                           <motion.span
                                               className={s.arrowWrapper}               // новий клас
                                               initial={false}
                                               animate={{rotate: openSections[title] ? 180 : 0}}
                                               transition={{duration: 0.2, ease: 'easeInOut'}}
                                               style={{originX: 0.5, originY: 0.5}}   // точка обертання всередині span
                                           >
                                            <SvgSelector id="language-dropdown-arrow"/>
                                          </motion.span>
                                        </span>
                                        </button>
                                        <AnimatePresence initial={false}>
                                            {openSections[title] && (
                                                <motion.div
                                                    className={`
                                            ${s.chapterPagesContainer} 
                                            ${openSections[title] ? s.open : ''}
                                        `}
                                                    variants={containerVariants}
                                                    initial="collapsed"
                                                    animate="open"
                                                    exit="collapsed"
                                                    style={{overflow: 'hidden'}}
                                                    onAnimationComplete={(definition) => {
                                                        if (
                                                            definition === "open" &&
                                                            !isInitialMount.current
                                                        ) {
                                                            const lastEl = lastLinkRefs.current[title];
                                                            lastEl?.scrollIntoView({
                                                                behavior: "smooth",
                                                                block: "nearest",
                                                            });
                                                        }
                                                    }}
                                                >
                                                    {pages.map(({icon_id, path, name}, idx) => {
                                                        const isLast = idx === pages.length - 1;

                                                        return (

                                                            <Link
                                                                to={path}
                                                                className={`${s.link} ${
                                                                    location.pathname.startsWith(path) ? s.active : ""
                                                                }`}
                                                                key={path}
                                                                ref={el => isLast && (lastLinkRefs.current[title] = el)}
                                                            >
                                                                <div className={s.linkIcon}>
                                                                    {isCollapsedSidebar === "true" ? (
                                                                        <SidebarMuiTooltip title={t(name)}>
                                                                            <SvgSelector id={icon_id} />
                                                                        </SidebarMuiTooltip>
                                                                    ) : (
                                                                        <SvgSelector id={icon_id} />
                                                                    )}
                                                                </div>
                                                                <span className={s.linkTitle}>{t(name)}</span>
                                                                {path === "/pixel-api" && !isValidPixelApiTokens && (
                                                                    <span className={s.alertContainer}>
                                                                <SvgSelector id="alert-circle-icon"/>
                                                            </span>
                                                                )}
                                                            </Link>
                                                        )
                                                    })}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                )
                            }
                        )}
                    </div>
                    <div className={s.bottomContainer}>

                        {sidebarOptionsList.slice(-1).map(({title, pages, icon}) => {
                                const isSectionActive = pages.some(page => location.pathname.startsWith(page.path));

                                return (
                                    <div
                                        className={`${s.chapterWrapper} ${isSectionActive ? s.active : ''}`}
                                        key={title}
                                    >
                                        <button
                                            className={s.chapterHeader}
                                            onClick={() => toggleSection(title)}
                                            type="button"
                                        >
                                            <div className={s.iconWrapper}>
                                                {isCollapsedSidebar === "true" ? (
                                                    <SidebarMuiTooltip title={t(title)}>
                                                        <SvgSelector id={icon} />
                                                    </SidebarMuiTooltip>
                                                ) : (
                                                    <SvgSelector id={icon} />
                                                )}
                                            </div>
                                            <span className={s.title}>{t(title)}</span>
                                            <span
                                                className={`${s.arrowIcon} ${openSections[title] ? s.open : ''}`}
                                            >
                                          <motion.span
                                              className={s.arrowIcon}
                                              style={{display: 'inline-block', transformOrigin: 'center'}}
                                              animate={{rotate: openSections[title] ? 180 : 0}}
                                              transition={{duration: 0.2, ease: 'easeInOut'}}
                                          >
                                            <SvgSelector id="language-dropdown-arrow"/>
                                          </motion.span>
                                        </span>
                                        </button>
                                        <AnimatePresence initial={false}>
                                            {openSections[title] && (
                                                <motion.div
                                                    className={`
                                            ${s.chapterPagesContainer} 
                                            ${openSections[title] ? s.open : ''}
                                        `}
                                                    key={title}
                                                    variants={containerVariants}
                                                    initial="collapsed"
                                                    animate="open"
                                                    exit="collapsed"
                                                    style={{overflow: 'hidden'}}
                                                >
                                                    {pages.map(({icon_id, path, name, href}) => {
                                                        const key = name;

                                                        return (

                                                            path
                                                                ? <Link
                                                                    to={path}
                                                                    className={`${s.link} ${
                                                                        location.pathname.startsWith(path) ? s.active : ""
                                                                    }`}
                                                                    key={key}
                                                                >
                                                                    <div className={s.linkIcon}>
                                                                        {isCollapsedSidebar === "true" ? (
                                                                            <SidebarMuiTooltip title={t(name)}>
                                                                                <SvgSelector id={icon_id}/>
                                                                            </SidebarMuiTooltip>
                                                                        ) : (
                                                                            <SvgSelector id={icon_id}/>
                                                                        )}
                                                                    </div>
                                                                    <span className={s.linkTitle}>{t(name)}</span>
                                                                </Link>
                                                                : <a
                                                                    href={href}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                    className={s.link}
                                                                    key={key}
                                                                >
                                                                    <div className={s.linkIcon}>
                                                                        {isCollapsedSidebar === "true" ? (
                                                                            <SidebarMuiTooltip title={t(name)}>
                                                                                <SvgSelector id={icon_id}/>
                                                                            </SidebarMuiTooltip>
                                                                        ) : (
                                                                            <SvgSelector id={icon_id}/>
                                                                        )}
                                                                    </div>
                                                                    <span className={s.linkTitle}>{t(name)}</span>
                                                                </a>

                                                        )
                                                    })}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                )
                            }
                        )}

                        <div className={s.exitBtnWrapper}>

                        <button
                            onClick={() => {
                                dispatch(setTopUpBalanceActive(false));
                                dispatch(setExitPopup(!exitPopup));
                                dispatch(setAdaptiveMenu(false));
                            }}
                            className={s.exitBtn}
                        >
                            <div className={s.linkIcon}>
                                {isCollapsedSidebar === "true" ? (
                                    <SidebarMuiTooltip title={t('logout')}>
                                        <SvgSelector id='logout-icon' />
                                    </SidebarMuiTooltip>
                                ) : (
                                    <SvgSelector id='logout-icon' />
                                )}
                            </div>
                            <span className={s.linkTitle}>{t('logout')}</span>
                        </button>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    )
        ;
};

export default NewSidebar;
